import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatLpDateRange,
  formatLpShortDate,
  canCuratePortfolioEvidence,
  portfolioIsStructurallyLocked,
} from '../../utils/workSamplesPage';
import PortfolioStatusBadges from './PortfolioStatusBadges';
import PortfolioReviewPanel from './PortfolioReviewPanel';
import { resolveSubmissionAttemptDisplay } from '../../utils/formSubmissionLifecycle';
import { buildUserProfileAssignmentUrl } from '../../utils/userProfileUrl';
import { formatCommentReasonLabels } from '../../constants/workSampleFlags';
import FlagCorrectionSla from './FlagCorrectionSla';

function trackKey(trackPeriodId) {
  return trackPeriodId ? String(trackPeriodId) : '__none__';
}

function ChevronIcon({ open }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={open ? 'ws-explorer-chevron ws-explorer-chevron--open' : 'ws-explorer-chevron'}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function portfolioDisplayStatus(lp) {
  if (!lp.has_portfolio || !lp.portfolio) return null;
  return (
    lp.portfolio.lifecycle_status ||
    lp.portfolio.display_status ||
    lp.attention?.display_portfolio_status ||
    lp.portfolio.status
  );
}

function portfolioOpenFlagCount(lp) {
  return lp.portfolio?.open_flag_count || lp.attention?.open_flag_count || 0;
}

function portfolioStatusLabel(lp) {
  if (!lp.has_portfolio || !lp.portfolio) return 'No portfolio yet';
  const lifecycle = portfolioDisplayStatus(lp);
  if (!lifecycle) return 'No portfolio yet';
  const labels = {
    draft: 'Draft',
    submitted: 'Submitted',
    in_review: 'In review',
    passed: 'Passed',
  };
  return labels[lifecycle] || lifecycle;
}

function AttentionBadges({ attention, displayStatus = null }) {
  const statusKey = displayStatus ? String(displayStatus).toLowerCase() : null;
  const reasons = (attention?.attention_reasons || []).filter((reason) => {
    if (reason.code === 'flagged_submission' && statusKey === 'flagged_submission') return false;
    if (reason.code === 'draft' && statusKey === 'draft') return false;
    return true;
  });
  if (!reasons.length) return null;

  return (
    <span className="ws-explorer-attention-badges">
      {reasons.map((reason) => (
        <span
          key={reason.code}
          className={`ws-explorer-attention-badge ws-explorer-attention-badge--${reason.severity || 'info'}`}
          title={reason.label}
        >
          {reason.label}
        </span>
      ))}
    </span>
  );
}

function OpenAssignmentIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14 3h7v7" />
      <path d="M10 14 21 3" />
      <path d="M5 5v14a2 2 0 0 0 2 2h14" />
    </svg>
  );
}

function OpenAssignmentLink({ profileLink, form, submissionId, label }) {
  if (!profileLink?.userId || !form?.id) return null;

  const url = buildUserProfileAssignmentUrl({
    userId: profileLink.userId,
    userGroupsPeriodsId: profileLink.userGroupsPeriodsId,
    formId: form.id,
    relationshipId: form.relationship_id,
    submissionId: submissionId || null,
    from: profileLink.returnPath,
  });

  const title =
    label ||
    (submissionId ? 'Open this submission on profile' : 'Open assignment on profile');

  return (
    <Link
      to={url}
      target="_blank"
      rel="noopener noreferrer"
      className="ws-explorer-open-assignment"
      title={`${title} (opens in new tab)`}
      aria-label={`${title} (opens in new tab)`}
    >
      <OpenAssignmentIcon />
    </Link>
  );
}

function normalizeSubmissionRow(sub) {
  if (!sub) return sub;
  const next = { ...sub };
  if (typeof next.answers === 'string') {
    try {
      next.answers = JSON.parse(next.answers);
    } catch {
      /* keep raw */
    }
  }
  if (typeof next.update_history === 'string') {
    try {
      next.update_history = JSON.parse(next.update_history);
    } catch {
      /* keep raw */
    }
  }
  return next;
}

function formatFlaggedAttemptSummary(submissions) {
  const flagged = submissions
    .map((sub, index) => {
      if (!sub.has_open_flags) return null;
      const normalizedSub = normalizeSubmissionRow(sub);
      const display = resolveSubmissionAttemptDisplay(
        normalizedSub,
        index,
        submissions.length
      );
      return display.attemptNumber ?? index + 1;
    })
    .filter((attempt) => attempt != null);

  if (!flagged.length) return null;
  if (flagged.length === 1) return `Flag on attempt ${flagged[0]}`;
  return `Flags on attempts ${flagged.join(', ')}`;
}

function buildFormStatusSummary(form, submissions) {
  const included =
    form.included_submissions?.length ||
    submissions.filter((sub) => sub.included_in_portfolio).length;
  const hasSubmission = form.has_submission || submissions.length > 0;
  const parts = [];

  if (submissions.length > 1) {
    parts.push(`${submissions.length} attempts`);
  }
  if (included > 0) {
    parts.push(`${included} in portfolio`);
  } else if (hasSubmission && submissions.length <= 1) {
    parts.push('Submitted — not in portfolio');
  } else if (!hasSubmission) {
    parts.push('Awaiting submission');
  } else if (hasSubmission) {
    parts.push('Submitted — not in portfolio');
  }

  const flagSummary = formatFlaggedAttemptSummary(submissions);
  if (flagSummary) parts.push(flagSummary);

  return parts.join(' · ');
}

function SubmissionFlagRows({ flags, asOfDate = null }) {
  if (!flags?.length) return null;

  return (
    <div className="ws-explorer-submission-flags">
      {flags.map((flag) => {
        const reasonLabels = formatCommentReasonLabels(flag.metadata);
        const reasonSummary = reasonLabels.length ? reasonLabels.join(', ') : '';
        return (
          <div key={flag.id} className="ws-explorer-flag-row">
            <strong>
              {flag.metadata?.title || reasonSummary || flag.type || 'Flag'}
            </strong>
            {flag.body ? <span>{flag.body}</span> : null}
            <FlagCorrectionSla flag={flag} asOfDate={asOfDate} />
          </div>
        );
      })}
    </div>
  );
}

function FormRow({
  form,
  portfolioId,
  canCurateEvidence,
  onToggleSubmission,
  togglingSubmissionId,
  profileLink,
  asOfDate = null,
}) {
  const submissions = form.student_submissions || [];
  const multiAttempt = submissions.length > 1;
  const [attemptsExpanded, setAttemptsExpanded] = useState(!multiAttempt);
  const status = buildFormStatusSummary(form, submissions);
  const showAttempts = submissions.length > 0 && (!multiAttempt || attemptsExpanded);

  return (
    <div
      className={[
        'ws-explorer-form-row',
        form.has_open_flags ? 'ws-explorer-form-row--flagged' : '',
        multiAttempt && !attemptsExpanded ? 'ws-explorer-form-row--collapsed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="ws-explorer-form-header">
        {multiAttempt && (
          <button
            type="button"
            className="ws-explorer-form-toggle"
            onClick={() => setAttemptsExpanded((prev) => !prev)}
            aria-expanded={attemptsExpanded}
            aria-label={
              attemptsExpanded
                ? `Collapse attempts for ${form.title || 'form'}`
                : `Expand attempts for ${form.title || 'form'}`
            }
          >
            <ChevronIcon open={attemptsExpanded} />
          </button>
        )}
        <div className="ws-explorer-form-main">
          <div className="ws-explorer-form-title-row">
            <strong>{form.title || `Form ${form.id}`}</strong>
            {form.has_open_flags && (
              <span className="ws-explorer-badge ws-explorer-badge--flag">
                {form.open_flag_count === 1 ? '1 flag' : `${form.open_flag_count} flags`}
              </span>
            )}
            <OpenAssignmentLink profileLink={profileLink} form={form} label="Open assignment on profile" />
          </div>
          <span className="ws-explorer-form-status">{status}</span>
          {form.evidence_mode && (
            <span className="ws-explorer-form-meta">{form.evidence_mode.replace(/_/g, ' ')}</span>
          )}
        </div>
      </div>
      {showAttempts && (
        <ul className="ws-explorer-submissions">
          {submissions.map((sub, index) => {
            const normalizedSub = normalizeSubmissionRow(sub);
            const display = resolveSubmissionAttemptDisplay(
              normalizedSub,
              index,
              submissions.length
            );
            const isIncluded = sub.included_in_portfolio;
            const isToggling = String(togglingSubmissionId) === String(sub.id);
            const actionBusy = togglingSubmissionId != null;

            return (
              <li
                key={sub.id}
                className={[
                  'ws-explorer-submission',
                  isIncluded ? 'ws-explorer-submission--included' : '',
                  sub.has_open_flags ? 'ws-explorer-submission--flagged' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="ws-explorer-submission-row">
                  <div className="ws-explorer-submission-main">
                    {display.attemptNumber != null && (
                      <span className="ws-explorer-submission-attempt">
                        Attempt {display.attemptNumber}
                      </span>
                    )}
                    {display.when && (
                      <span className="ws-explorer-submission-date">{display.when}</span>
                    )}
                    <span
                      className={
                        display.statusKey === 'submitted'
                          ? 'ws-explorer-submission-status ws-explorer-submission-status--submitted'
                          : 'ws-explorer-submission-status ws-explorer-submission-status--draft'
                      }
                    >
                      {display.statusLabel}
                    </span>
                    {display.scoreLabel && (
                      <span className="ws-explorer-submission-score">{display.scoreLabel}</span>
                    )}
                    {sub.has_open_flags && (
                      <span
                        className="ws-explorer-badge ws-explorer-badge--flag"
                        title={
                          sub.open_flags
                            ?.map((flag) => formatCommentReasonLabels(flag.metadata).join(', '))
                            .filter(Boolean)
                            .join('; ') || 'Open compliance flag'
                        }
                      >
                        {sub.open_flag_count === 1 ? 'Flagged' : `${sub.open_flag_count} flags`}
                      </span>
                    )}
                    {sub.has_open_flags && !isIncluded && (
                      <span className="ws-explorer-submission-flag-note">
                        Open flag remains if removed from evidence
                      </span>
                    )}
                  </div>
                  <div className="ws-explorer-submission-actions">
                    <OpenAssignmentLink
                      profileLink={profileLink}
                      form={form}
                      submissionId={sub.id}
                      label={`Open attempt ${display.attemptNumber ?? index + 1} on profile`}
                    />
                    {isIncluded && (
                      <span className="ws-explorer-badge ws-explorer-badge--included">In portfolio</span>
                    )}
                    {canCurateEvidence && portfolioId && (
                      <button
                        type="button"
                        className={
                          isIncluded
                            ? 'ws-explorer-submission-btn ws-explorer-submission-btn--remove'
                            : 'ws-explorer-submission-btn ws-explorer-submission-btn--include'
                        }
                        disabled={actionBusy}
                        aria-busy={isToggling}
                        title={
                          sub.has_open_flags && isIncluded
                            ? 'Removing from portfolio does not resolve open flags'
                            : undefined
                        }
                        onClick={() =>
                          onToggleSubmission?.({
                            portfolioId,
                            form,
                            submissionId: sub.id,
                            action: isIncluded ? 'remove' : 'include',
                          })
                        }
                      >
                        {isToggling ? 'Saving…' : isIncluded ? 'Remove' : 'Include'}
                      </button>
                    )}
                  </div>
                </div>
                <SubmissionFlagRows flags={sub.open_flags} asOfDate={asOfDate} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function LearningPeriodNode({
  lp,
  isExpanded,
  isHighlighted,
  onToggle,
  canAssign,
  formsCatalog,
  assignFormId,
  onAssignFormIdChange,
  showAssign,
  onToggleAssign,
  onAssign,
  assigning,
  canCurateEvidence,
  onToggleSubmission,
  togglingSubmissionId,
  profileLink,
  canSubmitPortfolios = false,
  canReviewPortfolios = false,
  portfolioActionBusy = false,
  onPortfolioAction,
  asOfDate = null,
}) {
  const displayStatus = portfolioDisplayStatus(lp);
  const openFlags = portfolioOpenFlagCount(lp);
  const structurallyLocked = portfolioIsStructurallyLocked(displayStatus);
  const effectiveCanCurate =
    canCurateEvidence &&
    canCuratePortfolioEvidence({
      lifecycleStatus: displayStatus,
      openFlagCount: openFlags,
      canCurate: true,
    });
  const allowAssign = canAssign && !structurallyLocked;
  const showCuration = effectiveCanCurate;

  return (
    <div
      className={[
        'ws-explorer-lp',
        !lp.is_student_track ? 'ws-explorer-lp--other-track' : '',
        isHighlighted ? 'ws-explorer-lp--highlight' : '',
        lp.attention?.needs_attention ? 'ws-explorer-lp--attention' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="ws-explorer-lp-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <ChevronIcon open={isExpanded} />
        <span className="ws-explorer-lp-title">{lp.title}</span>
        {lp.track_title && (
          <span
            className={
              lp.is_student_track
                ? 'ws-explorer-track-badge'
                : 'ws-explorer-track-badge ws-explorer-track-badge--muted'
            }
          >
            {lp.track_title}
          </span>
        )}
        <PortfolioStatusBadges
          lifecycleStatus={displayStatus}
          openFlagCount={openFlags}
          attentionLabel={lp.portfolio?.attention_label}
        />
        <AttentionBadges attention={lp.attention} displayStatus={displayStatus} />
        <span className="ws-explorer-lp-dates">
          {formatLpDateRange(lp.start_date, lp.end_date) || 'Dates TBD'}
        </span>
      </button>

      {isExpanded && (
        <div className="ws-explorer-lp-body">
          {lp.portfolio ? (
            <PortfolioReviewPanel
              lp={lp}
              portfolio={lp.portfolio}
              profileLink={profileLink}
              canSubmit={canSubmitPortfolios}
              canReview={canReviewPortfolios}
              busy={portfolioActionBusy}
              asOfDate={asOfDate}
              onSubmit={(id) => onPortfolioAction?.('submit', id)}
              onStartReview={(id) => onPortfolioAction?.('start-review', id)}
              onPass={(id) => onPortfolioAction?.('pass', id)}
            />
          ) : null}

          {!lp.has_portfolio && (
            <p className="profile-sheet-muted ws-explorer-empty">
              No portfolio record yet for this learning period. Assign a form to create one.
            </p>
          )}

          {(lp.portfolio?.forms || []).length > 0 && (
            <div className="ws-explorer-section">
              <p className="profile-sheet-label">Portfolio forms</p>
              {(lp.portfolio.forms || []).map((form) => (
                <FormRow
                  key={form.id}
                  form={form}
                  portfolioId={lp.portfolio?.id}
                  canCurateEvidence={showCuration}
                  onToggleSubmission={onToggleSubmission}
                  togglingSubmissionId={togglingSubmissionId}
                  profileLink={profileLink}
                  asOfDate={asOfDate}
                />
              ))}
            </div>
          )}

          {lp.has_portfolio && !(lp.portfolio?.forms || []).length && (
            <p className="profile-sheet-muted ws-explorer-empty">No forms assigned to this portfolio yet.</p>
          )}

          {(lp.portfolio?.documents || []).length > 0 && (
            <div className="ws-explorer-section">
              <p className="profile-sheet-label">Documents in portfolio</p>
              {(lp.portfolio.documents || []).map((doc) => (
                <div key={doc.id} className="ws-explorer-doc-row">
                  <strong>{doc.title || 'Document'}</strong>
                  {doc.category && <span>{doc.category}</span>}
                </div>
              ))}
            </div>
          )}

          {allowAssign && formsCatalog.length > 0 && (
            <div className="ws-explorer-assign">
              {!showAssign ? (
                <button
                  type="button"
                  className="profile-sheet-btn profile-sheet-btn--secondary ws-explorer-assign-toggle"
                  onClick={onToggleAssign}
                >
                  Assign form to this period…
                </button>
              ) : (
                <div className="ws-explorer-assign-form">
                  <label className="profile-sheet-label" htmlFor={`ws-explorer-assign-${lp.learning_period_id}`}>
                    Assign form
                  </label>
                  <div className="ws-explorer-assign-controls">
                    <select
                      id={`ws-explorer-assign-${lp.learning_period_id}`}
                      className="profile-sheet-select"
                      value={assignFormId}
                      onChange={(e) => onAssignFormIdChange(e.target.value)}
                      disabled={assigning}
                    >
                      <option value="">Select a form…</option>
                      {formsCatalog.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.title}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="profile-sheet-btn profile-sheet-btn--primary"
                      disabled={!assignFormId || assigning}
                      onClick={onAssign}
                    >
                      {assigning ? 'Assigning…' : 'Assign'}
                    </button>
                    <button
                      type="button"
                      className="profile-sheet-btn profile-sheet-btn--secondary"
                      disabled={assigning}
                      onClick={onToggleAssign}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function StudentPortfolioExplorer({
  explorer,
  loading,
  highlightLpId,
  highlightTrackKey,
  formsCatalog = [],
  canAssignForms = false,
  canCurateEvidence = false,
  profileLink = null,
  onAssign,
  onToggleSubmission,
  togglingSubmissionId = null,
  assigning = false,
  canSubmitPortfolios = false,
  canReviewPortfolios = false,
  portfolioActionBusy = false,
  onPortfolioAction,
  asOfDate = null,
}) {
  const showTrackFilter = explorer?.show_track_filter === true;
  const studentTrackKey = trackKey(explorer?.student_track_period_id);

  const [filterTrackKey, setFilterTrackKey] = useState('');
  const [filterLpId, setFilterLpId] = useState('');
  const [expandedLpIds, setExpandedLpIds] = useState(() => new Set());
  const [assignFormByLp, setAssignFormByLp] = useState({});
  const [showAssignLpId, setShowAssignLpId] = useState(null);

  useEffect(() => {
    if (!explorer) return;
    const defaultTrack = showTrackFilter
      ? highlightTrackKey || studentTrackKey
      : studentTrackKey;
    setFilterTrackKey(defaultTrack);
  }, [explorer, showTrackFilter, highlightTrackKey, studentTrackKey]);

  useEffect(() => {
    if (highlightLpId) {
      setFilterLpId(String(highlightLpId));
      setExpandedLpIds((prev) => new Set(prev).add(String(highlightLpId)));
    }
  }, [highlightLpId]);

  const trackOptions = useMemo(() => {
    if (!explorer?.tracks) return [];
    return explorer.tracks.map((t) => ({
      key: trackKey(t.track_period_id),
      title: t.track_title || 'No track',
      isStudentTrack: t.is_student_track,
      learningPeriods: t.learning_periods || [],
    }));
  }, [explorer?.tracks]);

  const activeTrack = useMemo(() => {
    if (!showTrackFilter) return null;
    const key = filterTrackKey || studentTrackKey;
    return trackOptions.find((t) => t.key === key) || trackOptions[0] || null;
  }, [trackOptions, filterTrackKey, showTrackFilter, studentTrackKey]);

  const lpOptions = useMemo(() => {
    if (!showTrackFilter) {
      return (explorer?.learning_periods || []).filter((lp) => lp.is_student_track);
    }
    if (!filterTrackKey) {
      return explorer?.learning_periods || [];
    }
    return activeTrack?.learningPeriods || [];
  }, [explorer?.learning_periods, showTrackFilter, filterTrackKey, activeTrack]);

  const visibleTracks = useMemo(() => {
    if (!showTrackFilter) return [];
    if (!filterTrackKey) return explorer?.tracks || [];
    return (explorer?.tracks || []).filter(
      (t) => trackKey(t.track_period_id) === filterTrackKey
    );
  }, [explorer?.tracks, showTrackFilter, filterTrackKey]);

  const handleTrackBadgeClick = (key) => {
    setFilterTrackKey((prev) => (prev === key ? '' : key));
    setFilterLpId('');
  };

  const handleLpBadgeClick = (lpId) => {
    const id = String(lpId);
    setFilterLpId((prev) => (prev === id ? '' : id));
    setExpandedLpIds((prev) => new Set(prev).add(id));
  };

  const toggleLp = (lpId) => {
    const key = String(lpId);
    setExpandedLpIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading && !explorer) {
    return null;
  }

  if (!explorer) {
    return <p className="profile-sheet-muted">Could not load portfolio data.</p>;
  }

  return (
    <div className="ws-explorer">
      <div className="ws-explorer-filter-bar ws-filter-bar">
        {showTrackFilter && trackOptions.length > 0 && (
          <div className="ws-filter-group">
            <span className="ws-filter-group-label">Track</span>
            {trackOptions.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`ws-filter-badge${filterTrackKey === t.key ? ' is-active' : ''}${
                  !t.isStudentTrack ? ' ws-filter-badge--muted' : ''
                }`}
                aria-pressed={filterTrackKey === t.key}
                onClick={() => handleTrackBadgeClick(t.key)}
              >
                {t.title}
              </button>
            ))}
          </div>
        )}

        {lpOptions.length > 0 && (
          <div className="ws-filter-group">
            <span className="ws-filter-group-label">Learning period</span>
            {lpOptions.map((lp) => {
              const lpKey = String(lp.learning_period_id);
              const dateHint = [formatLpShortDate(lp.start_date), formatLpShortDate(lp.end_date)]
                .filter(Boolean)
                .join(' – ');
              return (
                <button
                  key={lpKey}
                  type="button"
                  className={[
                    'ws-filter-badge',
                    filterLpId === lpKey ? 'is-active' : '',
                    lp.attention?.needs_attention ? 'ws-filter-badge--attention' : '',
                    lp.timing_status === 'current' ? 'ws-filter-badge--current' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={filterLpId === lpKey}
                  title={dateHint || lp.title}
                  onClick={() => handleLpBadgeClick(lp.learning_period_id)}
                >
                  {lp.title}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showTrackFilter && (
        <p className="ws-explorer-hint profile-sheet-muted">
          This student has portfolio work on another track. Other-track periods appear muted but remain
          accessible.
        </p>
      )}

      <div className="ws-explorer-tree">
        {visibleTracks.length === 0 && !showTrackFilter ? (
          <section className="ws-explorer-track">
            {(lpOptions.filter((lp) => !filterLpId || String(lp.learning_period_id) === filterLpId))
              .map((lp) => {
                const lpKey = String(lp.learning_period_id);
                return (
                  <LearningPeriodNode
                    key={lpKey}
                    lp={lp}
                    isExpanded={expandedLpIds.has(lpKey)}
                    isHighlighted={highlightLpId && String(highlightLpId) === lpKey}
                    onToggle={() => toggleLp(lpKey)}
                    canAssign={canAssignForms}
                    formsCatalog={formsCatalog}
                    assignFormId={assignFormByLp[lpKey] || ''}
                    onAssignFormIdChange={(val) =>
                      setAssignFormByLp((prev) => ({ ...prev, [lpKey]: val }))
                    }
                    showAssign={showAssignLpId === lpKey}
                    onToggleAssign={() =>
                      setShowAssignLpId((prev) => (prev === lpKey ? null : lpKey))
                    }
                    onAssign={() =>
                      onAssign?.(lp.learning_period_id, assignFormByLp[lpKey] || '')
                    }
                    assigning={assigning}
                    canCurateEvidence={canCurateEvidence}
                    onToggleSubmission={onToggleSubmission}
                    togglingSubmissionId={togglingSubmissionId}
                    profileLink={profileLink}
                    canSubmitPortfolios={canSubmitPortfolios}
                    canReviewPortfolios={canReviewPortfolios}
                    portfolioActionBusy={portfolioActionBusy}
                    onPortfolioAction={onPortfolioAction}
                    asOfDate={asOfDate}
                  />
                );
              })}
          </section>
        ) : (
          visibleTracks.map((track) => {
            const trackKeyVal = track.track_period_id ? String(track.track_period_id) : '__none__';
            let lps = track.learning_periods || [];
            if (filterLpId) {
              lps = lps.filter((lp) => String(lp.learning_period_id) === filterLpId);
            }
            if (!lps.length) return null;

            return (
              <section
                key={trackKeyVal}
                className={
                  track.is_student_track
                    ? 'ws-explorer-track'
                    : 'ws-explorer-track ws-explorer-track--other'
                }
              >
                {showTrackFilter && (
                  <h3 className="ws-explorer-track-title">
                    {track.track_title || 'No track'}
                    {!track.is_student_track && (
                      <span className="ws-explorer-track-note">Different track</span>
                    )}
                  </h3>
                )}
                {lps.map((lp) => {
                  const lpKey = String(lp.learning_period_id);
                  return (
                    <LearningPeriodNode
                      key={lpKey}
                      lp={lp}
                      isExpanded={expandedLpIds.has(lpKey)}
                      isHighlighted={highlightLpId && String(highlightLpId) === lpKey}
                      onToggle={() => toggleLp(lpKey)}
                      canAssign={canAssignForms}
                      formsCatalog={formsCatalog}
                      assignFormId={assignFormByLp[lpKey] || ''}
                      onAssignFormIdChange={(val) =>
                        setAssignFormByLp((prev) => ({ ...prev, [lpKey]: val }))
                      }
                      showAssign={showAssignLpId === lpKey}
                      onToggleAssign={() =>
                        setShowAssignLpId((prev) => (prev === lpKey ? null : lpKey))
                      }
                      onAssign={() =>
                        onAssign?.(lp.learning_period_id, assignFormByLp[lpKey] || '')
                      }
                      assigning={assigning}
                      canCurateEvidence={canCurateEvidence}
                      onToggleSubmission={onToggleSubmission}
                      togglingSubmissionId={togglingSubmissionId}
                      profileLink={profileLink}
                      canSubmitPortfolios={canSubmitPortfolios}
                      canReviewPortfolios={canReviewPortfolios}
                      portfolioActionBusy={portfolioActionBusy}
                      onPortfolioAction={onPortfolioAction}
                      asOfDate={asOfDate}
                    />
                  );
                })}
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}
